import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  MailCheck,
  Phone,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button, ButtonLink, Input, Label } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { FirstAccessValidation } from "../types/api";

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function formatPhone(value: string) {
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length > 11) digits = digits.slice(2);
  digits = digits.slice(0, 11);

  if (digits.length <= 2) return digits ? `(${digits}` : "";
  const areaCode = digits.slice(0, 2);
  const number = digits.slice(2);
  const firstPartLength = number.length > 8 ? 5 : 4;
  const firstPart = number.slice(0, firstPartLength);
  const secondPart = number.slice(firstPartLength);

  return `(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ""}`;
}

export function FirstAccessPage() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Informe seu e-mail corporativo.");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (![10, 11].includes(phoneDigits.length)) {
      setError("Informe um telefone válido com DDD.");
      return;
    }

    setLoading(true);
    try {
      await api.requestFirstAccess(email, phone);
      setSent(true);
    } catch (requestError) {
      setError(
        errorMessage(
          requestError,
          "Não foi possível solicitar o primeiro acesso. Tente novamente.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {sent ? (
        <div className="access-status access-status--success">
          <span className="access-status-icon"><MailCheck /></span>
          <p className="eyebrow">Verifique seu e-mail</p>
          <h2>Link de ativação enviado</h2>
          <p>
            Se <strong>{email}</strong> estiver apto ao primeiro acesso, você
            receberá uma mensagem com um link válido por 24 horas.
          </p>
          <div className="access-notice">
            <ShieldCheck />
            <span>A conta continuará inativa até que o link seja aberto e a senha criada.</span>
          </div>
          <ButtonLink to="/login" variant="secondary" size="lg" className="w-full">
            <ArrowLeft />
            Voltar ao login
          </ButtonLink>
        </div>
      ) : (
        <>
          <ButtonLink to="/login" variant="ghost" size="sm" className="auth-back-link">
            <ArrowLeft />
            Voltar
          </ButtonLink>
          <p className="eyebrow">Primeiro acesso</p>
          <h2>Crie sua conta</h2>
          <p className="login-subtitle">
            Use seu e-mail corporativo. O domínio precisa estar autorizado pela
            empresa no Helpdesk Mira.
          </p>

          <form onSubmit={submit} className="login-form">
            <div>
              <Label htmlFor="first-access-email">E-mail corporativo</Label>
              <div className="input-with-icon">
                <Mail />
                <Input
                  id="first-access-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="nome@suaempresa.com.br"
                  aria-invalid={Boolean(error)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="first-access-phone">Telefone com DDD</Label>
              <div className="input-with-icon">
                <Phone />
                <Input
                  id="first-access-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(formatPhone(event.target.value))}
                  placeholder="(41) 99999-9999"
                  maxLength={15}
                  aria-invalid={Boolean(error)}
                />
              </div>
            </div>
            {error && <p className="auth-form-error" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={loading} className="w-full">
              Enviar link de ativação
              {!loading && <ArrowRight />}
            </Button>
          </form>

          <div className="access-notice access-notice--muted">
            <ShieldCheck />
            <span>Por segurança, nenhum acesso é liberado sem a confirmação pelo e-mail.</span>
          </div>
        </>
      )}
    </AuthLayout>
  );
}

export function ActivateFirstAccessPage() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );
  const [validation, setValidation] = useState<FirstAccessValidation | null>(null);
  const [checking, setChecking] = useState(Boolean(token));
  const [invalidMessage, setInvalidMessage] = useState(
    token ? "" : "O link de ativação está incompleto.",
  );
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) return undefined;

    api.validateFirstAccessToken(token)
      .then((result) => {
        if (active) setValidation(result);
      })
      .catch((validationError) => {
        if (active) {
          setInvalidMessage(
            errorMessage(
              validationError,
              "Este link de ativação é inválido ou expirou.",
            ),
          );
        }
      })
      .finally(() => {
        if (active) setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const rules = {
    length: password.length >= 8 && password.length <= 50,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const passwordIsValid = Object.values(rules).every(Boolean);
  const passwordsMatch = password.length > 0 && password === confirmation;

  async function activate(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!passwordIsValid) {
      setError("Crie uma senha que atenda a todos os requisitos.");
      return;
    }
    if (!passwordsMatch) {
      setError("A confirmação da senha não coincide.");
      return;
    }

    setLoading(true);
    try {
      await api.activateFirstAccess(token, password);
      setActivated(true);
    } catch (activationError) {
      setError(
        errorMessage(
          activationError,
          "Não foi possível ativar seu acesso. Solicite um novo link.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {checking ? (
        <div className="access-status">
          <span className="loader-orbit" />
          <h2>Validando seu link</h2>
          <p>Estamos verificando a segurança da solicitação.</p>
        </div>
      ) : invalidMessage ? (
        <div className="access-status access-status--error">
          <span className="access-status-icon"><XCircle /></span>
          <p className="eyebrow">Link indisponível</p>
          <h2>Não foi possível continuar</h2>
          <p>{invalidMessage}</p>
          <ButtonLink to="/primeiro-acesso" size="lg" className="w-full">
            Solicitar novo link
          </ButtonLink>
          <ButtonLink to="/login" variant="ghost" size="lg" className="w-full">
            Voltar ao login
          </ButtonLink>
        </div>
      ) : activated ? (
        <div className="access-status access-status--success">
          <span className="access-status-icon"><CheckCircle2 /></span>
          <p className="eyebrow">Tudo pronto</p>
          <h2>Seu acesso foi ativado</h2>
          <p>Agora você já pode entrar usando seu e-mail e a senha que acabou de criar.</p>
          <ButtonLink to="/login" size="lg" className="w-full">
            Ir para o login
            <ArrowRight />
          </ButtonLink>
        </div>
      ) : (
        <>
          <p className="eyebrow">Ativação segura</p>
          <h2>Crie sua senha</h2>
          <p className="login-subtitle">
            Confirme o primeiro acesso de <strong>{validation?.email}</strong>.
          </p>

          <form onSubmit={activate} className="login-form">
            <div>
              <Label htmlFor="new-password">Nova senha</Label>
              <div className="input-with-icon">
                <KeyRound />
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Digite uma senha segura"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="password-rules" aria-label="Requisitos da senha">
              <span className={rules.length ? "is-valid" : ""}><Check /> 8 a 50 caracteres</span>
              <span className={rules.upper ? "is-valid" : ""}><Check /> Uma letra maiúscula</span>
              <span className={rules.lower ? "is-valid" : ""}><Check /> Uma letra minúscula</span>
              <span className={rules.number ? "is-valid" : ""}><Check /> Um número</span>
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirme a senha</Label>
              <div className="input-with-icon">
                <KeyRound />
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder="Digite novamente"
                  aria-invalid={confirmation.length > 0 && !passwordsMatch}
                />
              </div>
              {confirmation.length > 0 && (
                <p className={`password-match ${passwordsMatch ? "is-valid" : ""}`}>
                  {passwordsMatch ? <CheckCircle2 /> : <XCircle />}
                  {passwordsMatch ? "As senhas coincidem" : "As senhas ainda não coincidem"}
                </p>
              )}
            </div>

            {error && <p className="auth-form-error" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={loading} className="w-full">
              Ativar meu acesso
              {!loading && <ArrowRight />}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
